// BatchPanel.js
// The check a file is in, in the review dialog's file view (Spec 2 section 6.4
// item 2, with Spec 3 section 7.3's words): read from the batch document while
// the file's batch has not landed.
//
//   running  being checked now; nothing can be changed until it finishes
//   parked   the check raised questions: Resolve conflicts, and "Take files out
//            of this check"; while answers are being submitted, neither
//   waiting  queued for the next check; "Take files out of this check"
//
// Resolve conflicts opens the batch's questions (QuestionsDialog), which stays
// open, following the batch, until the person closes it. "Take files out of
// this check" lists the batch's files with checkboxes; its confirmation calls
// rewindFiles once for the chosen files, so several leave the batch together
// before the next check takes it.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { gestureFailureMessage, rewindFiles } from '../../../services/cleaningGestures';
import { countNoun } from '../../../utils/pluralise';
import QuestionsDialog from './QuestionsDialog';

function TakeFilesOutDialog({ open, batch, fileNames, currentFileId, onClose, onConfirm }) {
  const [chosen, setChosen] = useState(() => new Set([currentFileId]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (fileId) => setChosen((previous) => {
    const next = new Set(previous);
    if (next.has(fileId)) next.delete(fileId);
    else next.add(fileId);
    return next;
  });

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      // In the batch's order, and only files still in it.
      await onConfirm(batch.files.filter((fileId) => chosen.has(fileId)));
    } catch (failure) {
      setError(gestureFailureMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Take files out of this check</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          The files you choose are un-approved and leave the check; the others wait for the next one.
          Approve a file again to have it checked.
        </DialogContentText>
        <FormGroup>
          {batch.files.map((fileId) => (
            <FormControlLabel
              key={fileId}
              control={<Checkbox checked={chosen.has(fileId)} onChange={() => toggle(fileId)} disabled={busy} />}
              label={fileNames[fileId] || 'A file'}
            />
          ))}
        </FormGroup>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={confirm} disabled={busy || chosen.size === 0}>
          {busy ? <CircularProgress size={18} color="inherit" /> : 'Take out'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BatchPanel({ companyId, tableId, table, file, batch, fileNames, tableName, onNotice }) {
  const [takeOutOpen, setTakeOutOpen] = useState(false);
  const [questionsOpen, setQuestionsOpen] = useState(false);
  const submitting = Boolean(batch.submitting);
  // A parked batch with no submission under way, or one waiting for the next check.
  const canTakeOut = (batch.status === 'parked' && !submitting) || batch.status === 'waiting';

  // The check moved on while the dialog was open (it started, or answers began to
  // be submitted): close it without taking anything out.
  useEffect(() => {
    if (takeOutOpen && !canTakeOut) {
      setTakeOutOpen(false);
      onNotice('The check this file is in has moved on, so no file was taken out.');
    }
  }, [takeOutOpen, canTakeOut, onNotice]);

  const mates = batch.files.filter((fileId) => fileId !== file.id).map((fileId) => fileNames[fileId]).filter(Boolean);
  const together = mates.length > 0 ? `, together with ${mates.join(', ')}` : '';

  let message;
  if (batch.status === 'running') {
    message = `Being checked against ${tableName} now${together}. Nothing can be changed until the check finishes.`;
  } else if (batch.status === 'parked' && submitting) {
    message = 'Answers are being submitted.';
  } else if (batch.status === 'parked') {
    const answered = batch.answered_questions > 0
      ? `; ${countNoun(batch.answered_questions, 'row assignment')} saved`
      : '';
    message = `The check raised ${countNoun(batch.open_questions, 'question')} about this upload${answered}.`;
  } else {
    message = `Queued to be checked against ${tableName}${together}.`;
  }

  const takeOut = async (fileIds) => {
    await rewindFiles(companyId, tableId, batch.id, fileIds);
    setTakeOutOpen(false);
    onNotice(`${countNoun(fileIds.length, 'file')} taken out of the check.`);
  };

  return (
    <>
      <Alert
        severity={batch.status === 'parked' ? 'warning' : 'info'}
        icon={batch.status === 'running' || submitting ? <CircularProgress size={18} /> : undefined}
        sx={{ mb: 1.5 }}
        action={(
          <Box sx={{ display: 'flex', gap: 1 }}>
            {batch.status === 'parked' && !submitting && (
              <Button size="small" variant="contained" color="secondary" onClick={() => setQuestionsOpen(true)}>
                Resolve conflicts
              </Button>
            )}
            {canTakeOut && (
              <Button size="small" color="inherit" onClick={() => setTakeOutOpen(true)}>
                Take files out of this check
              </Button>
            )}
          </Box>
        )}
      >
        {message}
      </Alert>
      {questionsOpen && (
        <QuestionsDialog
          open
          companyId={companyId}
          tableId={tableId}
          table={table}
          batch={batch}
          fileNames={fileNames}
          onClose={() => setQuestionsOpen(false)}
        />
      )}
      {takeOutOpen && (
        <TakeFilesOutDialog
          open
          batch={batch}
          fileNames={fileNames}
          currentFileId={file.id}
          onClose={() => setTakeOutOpen(false)}
          onConfirm={takeOut}
        />
      )}
    </>
  );
}

export default BatchPanel;
